<!--
 * @Description: 访客计划 - 新增编辑表单
 * @Author: mdb
 * @Date: 2022-07-19 11:15:19
 * @LastEditTime: 2023-03-14 17:24:50
 * @LastEditors: cxh
-->
<template>
  <a-form scrollToFirstError>
    <dc-layout-card :title="props.title" :span="6">
      <dc-form-item label="客户名称" v-bind="validateInfos.visitRefId">
        <ClueAndCustomerSelect
          appId="cos_fbu"
          v-model:value="dataForm.visitRefId"
          v-model:label="dataForm.customerName"
          :disabled="!props.isEdit || !!props.saleChanceRefId"
          :view="props.type === 'view'"
          @change="handleCustomerNameChange"
        />
      </dc-form-item>
      <dc-form-item label="客户ID" v-if="props.type === 'view'">
        <CosInput v-model:value="dataForm.visitRefId" view />
      </dc-form-item>
      <dc-form-item label="计划标题" v-bind="validateInfos.title">
        <CosInput
          v-model:value="dataForm.title"
          :maxlength="100"
          :disabled="!props.isEdit"
          :view="props.type === 'view'"
        />
      </dc-form-item>
      <dc-form-item label="拜访时间" v-bind="validateInfos.startTime">
        <CosSingleDateTimePicker
          v-model:startTime="dataForm.startTime"
          v-model:endTime="dataForm.endTime"
          :isRange="true"
          :view="props.type === 'view'"
        />
      </dc-form-item>
      <dc-form-item label="拜访目的" v-bind="validateInfos.purpose">
        <ParamCodesSelect
          v-model:value="dataForm.purpose"
          :options="paramCodeData.fbu_follow_purpose"
          :disabled="!props.isEdit"
          :view="props.type === 'view'"
          allowClear
        />
      </dc-form-item>
      <dc-form-item label="拜访方式" v-bind="validateInfos.mode">
        <ParamCodesSelect
          v-model:value="dataForm.mode"
          :options="paramCodeData.fbu_follow_mode"
          :disabled="!props.isEdit"
          :view="props.type === 'view'"
          allowClear
        />
      </dc-form-item>
      <dc-form-item label="关联商机" v-bind="validateInfos.chanceId">
        <BusinessName appId="cos_fbu" v-model:value="dataForm.chanceName" v-if="props.type === 'view'" />
        <CosSelect
          v-else
          v-model:value="dataForm.chanceId"
          :disabled="!props.isEdit"
          :view="props.type === 'view'"
          allowClear
          @change="handleChanceChange"
        >
          <a-select-option
            v-for="item in chanceEnumComputed"
            :key="item.chanceId"
            :value="item.chanceId"
            :disabled="item.disabled"
          >
            <BusinessName appId="cos_fbu" v-model:label="item.label" v-model:value="item.saleChanceName" />
          </a-select-option>
        </CosSelect>
      </dc-form-item>
      <dc-form-item label="访客提醒">
        <CosSelect
          v-model:value="dataForm.remindType"
          :options="remindTypeEnum"
          :disabled="!props.isEdit"
          :view="props.type === 'view'"
          allowClear
        />
      </dc-form-item>
      <dc-form-item label="提醒时间" v-bind="validateInfos.remindTime" v-if="isOtherTime">
        <CosSingleDateTimePicker
          v-model:startTime="dataForm.remindTime"
          :isRange="false"
          :view="props.type === 'view'"
        />
      </dc-form-item>
      <dc-form-item label="协同人">
        <UserSelect
          v-model:value="dataForm.fbuAssistPersons"
          mode="multiple"
          :max-tag-count="!props.isEdit ? 100 : 'responsive'"
          :disabled="!props.isEdit"
          :view="props.type === 'view'"
          allowClear
        />
      </dc-form-item>
      <dc-form-item label="访客计划完成时间" v-if="!props.isEdit">
        <a-date-picker
          style="width: 100%"
          v-model:value="dataForm.completeTime"
          format="YYYY/MM/DD HH:mm:ss"
          value-format="YYYY/MM/DD HH:mm:ss"
          show-time
          disabled
          placeholder="访客计划完成时间"
          v-if="props.type !== 'view'"
        />
        <CosInput v-model:value="dataForm.completeTime" view v-else />
      </dc-form-item>
      <template v-if="props.type === 'view'">
        <dc-form-item label="创建人">
          <CosInput v-model:value="dataForm.createByName" view />
        </dc-form-item>
        <dc-form-item label="创建时间">
          <CosInput v-model:value="dataForm.createTime" view />
        </dc-form-item>
      </template>
      <a-col :span="24" />
      <dc-form-item label="备注" :span="12">
        <CosTextarea
          v-model:value="dataForm.remark"
          :maxlength="2000"
          :disabled="!props.isEdit"
          :view="props.type === 'view'"
        />
      </dc-form-item>
    </dc-layout-card>
  </a-form>
</template>

<script setup lang="ts">
import { reactive, watch, toRaw, ref, onMounted, computed } from 'vue'
import { cloneDeep, isEqual } from 'lodash-es'
import { Form } from 'ant-design-vue'
import ClueAndCustomerSelect from '@/components/ClueAndCustomerSelect/index.vue'
import BusinessName from '@/components/BusinessName/index.vue'
import CosSingleDateTimePicker from '@/components/CosSingleDateTimePicker/index.vue'
import { remindTypeEnum } from '../dataConfig'
import { listByParamCodes } from '@/api/common'
import { businessSelectListByRefId } from '@/api/gucangSales/chanceManage'
import { selectForNumberValidate } from '@/utils/validate'
import { paramCodesDataFilter } from '@/utils/index'
import type { ParamCodeItem } from '@/typings/common'
import type { FbuVisitorProgramEditParams } from '@/typings/api/followUpManage'
const useForm = Form.useForm

const props = defineProps({
  value: Object,
  title: String,
  saleChanceRefId: [String, Number], // 客户id，当传入有值的时候需要设置客户名称不可选
  isEdit: {
    type: Boolean,
    default: true
  },
  type: {
    type: String,
    default: 'add'
  }
})

const querySaleChanceRefId = computed(() => props.saleChanceRefId)
const paramCodeData: {
  [key: string]: ParamCodeItem[]
} = reactive({
  fbu_follow_purpose: [], // 拜访目的
  fbu_follow_mode: [] // 拜访方式（跟进方式）
})
// 获取业务参数
const getByParamCodes = () => {
  listByParamCodes({
    appId: 'cos_fbu',
    paramCodes: ['fbu_follow_purpose', 'fbu_follow_mode']
  }).then((res) => {
    if (props.type !== 'view') {
      // 递归过滤业务参数状态
      res.data = paramCodesDataFilter(res.data, 0)
    }
    Object.assign(paramCodeData, res.data)
  })
}
onMounted(() => getByParamCodes())

const dataForm = reactive<FbuVisitorProgramEditParams>({
  visitRefId: undefined,
  title: undefined,
  startTime: undefined,
  purpose: undefined,
  mode: undefined,
  chanceId: undefined
})
const isOtherTime = ref(false) // 是否其它时间
// // 动态判断“关联商机”是否必填
// const isAberrant = computed(() => {
//   if (dataForm.purpose) {
//     const find = paramCodeData.fbu_follow_purpose.find((e) => e.id === dataForm.purpose)
//     return find?.name !== '处理异常' && chanceEnumComputed.value.length > 0
//   } else {
//     return false
//   }
// })
const formRules = reactive({
  visitRefId: [{ required: true, validator: selectForNumberValidate, message: '请选择客户名称' }],
  title: [{ required: true, message: '请输入计划标题' }],
  startTime: [{ required: true, message: '请选择拜访时间' }],
  purpose: [{ required: true, validator: selectForNumberValidate, message: '请选择拜访目的' }],
  mode: [{ required: true, validator: selectForNumberValidate, message: '请选择拜访方式' }],
  // chanceId: [{ required: isAberrant, message: '当目的不是[处理异常]且存在商机时,[关联商机]必填' }],
  remindTime: [{ required: isOtherTime, message: '请选择提醒时间' }]
})

// 获取关联商机
const chanceEnum = ref<any[]>([])
const getChanceEnum = (id: string) => {
  businessSelectListByRefId(id).then((res) => {
    chanceEnum.value = res.data
  })
}
const handleCustomerNameChange = (value?: any) => {
  dataForm.chanceName = undefined
  dataForm.chanceId = undefined
  if (!value) {
    chanceEnum.value = []
  }
}
const handleChanceChange = (value?: any) => {
  dataForm.chanceName = value ? chanceEnum.value.find((e) => e.chanceId === value)?.label : undefined
}
const chanceEnumComputed = computed(() => {
  if (props.type === 'add') {
    // 可选该客户下状态为 “初步洽谈、需求确认、制定方案、达成合作”的商机
    return chanceEnum.value.filter((e) => [1, 2, 4, 5].includes(e.status))
  } else if (props.type === 'edit') {
    return chanceEnum.value.filter((e) => {
      if (e.chanceId === defaultChanceId.value && [6, 7, 8].includes(e.status)) {
        e.disabled = true
      }
      return e.chanceId === defaultChanceId.value || [1, 2, 4, 5].includes(e.status)
    })
  } else {
    return chanceEnum.value
  }
})
// 记录编辑前的商机数据
const defaultChanceId = ref('')
watch(
  () => dataForm.chanceId,
  (newVal, oldVal) => {
    if (!defaultChanceId.value && oldVal == null && newVal) {
      defaultChanceId.value = newVal
    }
  }
)

const emit = defineEmits(['update:value'])
watch(
  () => props.value,
  (val) => {
    if (!val) return
    // 如果值相同则不更新
    const isSame = isEqual(toRaw(dataForm), toRaw(val))
    if (isSame) return
    const clone = cloneDeep(val)
    if (clone.chanceId === '0') {
      clone.chanceId = undefined
    }
    Object.assign(dataForm, clone)
  },
  { deep: true, immediate: true }
)
watch(
  dataForm,
  (val) => {
    emit('update:value', val)
  },
  { deep: true }
)
// 回显数据监听客户名称变化
watch(
  () => dataForm.visitRefId,
  (val) => {
    if (val) {
      getChanceEnum(val)
    }
  },
  { immediate: true }
)
watch(
  querySaleChanceRefId,
  (val) => {
    if (val) {
      dataForm.visitRefId = val as string
    }
  },
  { immediate: true }
)
// 监听判断“提醒时间”是否必填
// 当访客提醒选择其他时间时，“提醒时间”字段必填
watch(
  () => dataForm.remindType,
  (val) => {
    setTimeout(() => clearValidate(['remindTime']), 0)
    isOtherTime.value = val === 4
    if (val && val !== 4) dataForm.remindTime = undefined
  },
  { immediate: true }
)
watch(
  () => dataForm.chanceId,
  (val) => {
    setTimeout(() => clearValidate(['chanceId']), 0)
  }
)

const { resetFields, validate, clearValidate, validateInfos } = useForm(dataForm, formRules)
defineExpose({ resetFields, validate, clearValidate })
</script>
<script lang="ts">
export default { name: 'VisitorProgramForm' }
</script>

<style lang="scss" scoped></style>
